<template>
  <validation-observer
    ref="form"
    v-slot="{
      valid,
      invalid,
      errors,
      dirty,
      pending,
      pristine,
      failed
    }"
    :tag="tag"
    :disabled="disabled"
    class="z-form"
  >
    <slot
      :pending="pending"
      :dirty="dirty"
      :errors="errors"
      :valid="valid"
      :invalid="invalid"
      :pristine="pristine"
      :failed="failed"
    ></slot>
  </validation-observer>
</template>

<script>
  /**
   * v-slot
   *
   * dirty [Boolean] - True if at least one field is dirty.
   * pristine	[Boolean] - True if all fields are pristine (not dirty).
   * valid	[Boolean] - True if all fields are valid.
   * invalid	[Boolean] - True if at least one field is invalid.
   * pending	[Boolean] - True if at least one field's validation is in progress.
   * touched	[Boolean] - True if at least one field has been touched (blurred).
   * untouched	[Boolean] - True if all fields haven't been touched (blurred).
   * passed	[Boolean] - True if all fields are valid as a result of user interaction
   * failed	[Boolean] - True if any field is invalid as a result of user interaction
   * errors	{ [x: string]: string[] } - An object containing reference to each field errors, each field is keyed by its vid prop.
   * validate	() => Promise<boolean> - A method that triggers validation for all providers. Mutates child providers state unless silent is true.
   * handleSubmit	(cb: Function) => Promise<void> -	Calls validation like validate and mutates provider's state, accepts a callback to be run only if the validation is successful.
   * reset	() => void - A method that resets validation state for all providers.
   *
   */

  import transform from 'lodash-es/transform';

  export default {
    name: 'ZForm',
    props: {
      tag: {
        type: String,
        default: 'form'
      },

      disabled: {
        type: Boolean,
        default: false
      }
    },

    methods: {
      validate: function() {
        return this.$refs.form.validate();
      },

      reset: function() {
        return this.$refs.form.reset();
      },

      setErrors: function(errors) {
        return this.$refs.form.setErrors(errors);
      },

      getFields: function() {
        return transform(this.$refs.form.fields, (result, value, key) => {
          result[key] = value.name || key;
        });
      },

      checkEnterPress(event) {
        if (event.key === 'Enter' && event.target.id === 'recipients_phone') {
          event.preventDefault();
        }
      }
    },

    mounted() {
      window.addEventListener('keypress', this.checkEnterPress);
    },

    destroyed() {
      window.removeEventListener('keypress', this.checkEnterPress);
    }
  };
</script>

<style lang="scss">
  .z-form {
    position: relative;

    &--validate {
      position: relative;

      &:after {
        content: '';
        display: inline;
        position: absolute;
        width: 18px;
        height: 18px;
        border: 2px solid $grey-dark;
        border-radius: 100%;
        right: -40px;
        top: 0;
      }
    }

    &--invalid {
      &:after {
        border-color: $red;
      }
    }

    &--valid {
      &:after {
        border-color: #28d094;
        background-image: url('/img/icons/form-state-valid.svg');
        background-repeat: no-repeat;
        background-position: 1px 4px;
      }
    }

    &--vertical {
      label {
        display: inline-block;
        width: 100%;
        margin-bottom: $spacer/2;
      }
    }
  }
</style>
